import { FieldConfig, FieldType, ValidationRule, ValidationType } from "../types/formTypes";
import { safeGet } from "../utils/safeGet";

export interface TuitionFormData {
  institution: any;
  courseType: any;
  totalCost: any;
  courseDescription: any;
  relevanceToRole: any;
  // Personal Info
  empId: number;
  empNuid: string;
  empLastName: string;
  empFirstName: string;
  empMiddleName?: string;
  empWorkPhone?: number;
  empWorkEmail: string;
  empAddress1: string;
  empAddress2?: string;
  empCity: string;
  empState: string;
  empPostalZip: number;
  altPhone?: number;
  altEmail?: string;
  empHomePhone?: number;
  empAlternatePhone?: number;
  empPrefPhone?: string;
  empHomeEmail?: string;
  empPrefEmail?: string;

  // Employment Info
  shiftStdHours?: number;
  empHireDate?: string;
  empRehireDate?: string;
  empJobStatus?: string;
  empType?: string;
  jobTitleCode?: number;
  jobTitleName?: string;
  unionName?: string;
  fullPartTime?: string;
  regularTemporary?: string;
  bargUnitDesc?: string;
  empShift?: string;
  mgrNuid?: string;
  mgrName?: string;
  mgrEmail?: string;
  mgrPhone?: string;

  // Primary Approver Info
  managerNuid?: string;
  managerName?: string;
  managerEmail?: string;
  managerPhone?: string;
  isSearchBtnRequired?: boolean;
  isAltField?: boolean;

  // Alternate Approver Info
  alternateNuid?: string;
  alternateName?: string;
  alternateEmail?: string;
  alternatePhone?: string;

  // Vendor/School Info
  schoolName?: string;
  programField?: string;
  programType?: string;
  applicationFundingSource?: string;
  degreeName?: string;
  termStartDate?: string;
  termEndDate?: string;
  applicationTerm?: string;
  schoolContactUrl?: string;
  applicationYear?: string;
  degreeFinishYear?: string;

  // Class Info
  [key: string]:
    | string
    | number
    | boolean
    | Record<string, any>
    | Record<string, any>[]
    | undefined;
  classes?: Array<Record<string, any>>;
  courseName?: string;
  reimbursementType?: string;
  deliveryType?: string;
  courseProgramType?: string;
  participantsConditions?: string;
  applicationSubmitted?: string;
  courseStartDate?: string;
  courseEndDate?: string;
  courseCreditsUnitsHours?: string;
  tuitionFee?: number;
  entranceRegistrationFees?: number;
  esitmatedBooksFee?: number;
  labFees?: number;
  otherFees?: number;
  totalEstimatedCourseFees?: number;
  classSchoolName?: string;

  // Travel Info
  estimatedAirfareBaggageFee?: number;
  estimatedAutoRentalFee?: number;
  estimatedParkTollFee?: number;
  estimatedPublicTransportFee?: number;
  estimatedHotelFee?: number;

  // Miles Info
  totalNumberOfMiles?: number;
  estimatedMilesDollarValue?: number;
  originAddress?: string;
  originCity?: string;
  originState?: string;
  originZipCode?: string;
  destinationAddress?: string;
  numberOfTravelDays?: number;
  milesTotalEstimatedCourseFees?: number;
  milesSchoolName?: string;

  // Comments Info
  employeeComments?: string;
  employmentJobComments?: string;
  noEstimatedFees?: string;

  // Participants Conditions Info
  tuitionPolicyAgreement?: string;
  managerArrangements?: string;
}

// ==================== CONSTANTS ====================
const nonEditableHelperText: string =
  "Read only; this value is assigned automatically by HR";

const READONLY_HELPER_TEXT =
  "Read only; this value is automatically calculated once fees are entered";

const NUMERIC_PLACEHOLDER = "i.e. 530";
const NUMERIC_HELPER_TEXT = "Edit field: numbers only";
const NUMERIC_MIN_ERROR = "Enter a valid numeric amount.";
const NUMERIC_MAX_ERROR = (label: string) => `${label} is an invalid amount.`;

const PHONE_PLACEHOLDER = "i.e. 123-456-7890";
const PHONE_HELPER_TEXT = "Edit field; must have 10 digits";
const PHONE_ERROR = "Invalid phone number. Please enter a 10-digit phone number.";

const DATE_HELPER_TEXT = "Edit field; MM/DD/YYYY";
const DATE_PLACEHOLDER = "i.e. 10/07/2025";
const DATE_ERROR_TEXT = "Invalid date format.";
const DATE_FORMAT_ERROR = "Invalid date format. Use MM/DD/YYYY";

const ZIP_CODE_HELPER_TEXT = "Edit field; must be 5 digits";
const ZIP_CODE_PLACEHOLDER = "i.e. 97201";
const ZIP_CODE_ERROR = "Zip code must be exactly 5 digits.";
const ZIP_CODE_REGEX = /^\d{5}$/;

const TEXTAREA_MAX_LENGTH = 500;
const TEXTAREA_MAX_ERROR = (label: string) =>
  `${label} must not exceed 500 characters.`;

const LOREM_PLACEHOLDER =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Tristique euismod ultricies adipiscing eu eget purus. Id leo hendrerit eget mi id hac egestas semper. Hac vitae facilisi sodales lectus vitae. Id enim turpis in vitae molestie sit. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Tristique euismod ultricies adipiscing eu eget purus. Id leo hendrerit eget mi id hac egestas semper. Hac vitae facilisi sodales lectus vitae. Id enim turpis in vitae molestie sit.Lorem ipsum dolor sit amet, consectetur adipiscing elit. Tristique euismod ultricies adipiscing eu eget purus. Id leo hendrerit egetmi id hac egestas semper. Hac vitae facilisi sodales lectus vitae. Id enim turpis in vitae molestie sit.";

const SELECT_PLACEHOLDER = "--Select--";

// ==================== FIELD FACTORY FUNCTIONS ====================

/**
 * Creates a read-only HR field with standard configuration
 */
const createHRField = (
  name: string,
  label: string,
  fieldType: FieldType = "text" as FieldType,
  fieldGroupType?: string
): FieldConfig => {
  const field: FieldConfig = {
    name,
    label,
    type: fieldType,
    disabled: true,
    helperText: nonEditableHelperText,
  };
  if (fieldGroupType) {
    field.fieldGroupType = fieldGroupType;
  }
  return field;
};

/**
 * Creates a numeric fee field with standard validation
 */
const createFeeField = (
  name: string,
  label: string,
  fieldGroupType?: string
): FieldConfig => {
  const validations: ValidationRule[] = [
    {
      type: "min" as ValidationType,
      value: 0,
      message: NUMERIC_MIN_ERROR,
    },
    {
      type: "max" as ValidationType,
      value: 99999.99,
      message: NUMERIC_MAX_ERROR(label),
    },
  ];

  const field: FieldConfig = {
    name,
    label,
    type: "number" as FieldType,
    required: false,
    helperText: NUMERIC_HELPER_TEXT,
    placeholder: NUMERIC_PLACEHOLDER,
    validations,
  };
  if (fieldGroupType) {
    field.fieldGroupType = fieldGroupType;
  }
  return field;
};

/**
 * Creates a phone number field with standard validation
 */
const createPhoneField = (
  name: string,
  label: string,
  disabled: boolean = false,
  isAltField: boolean = false,
  fieldGroupType?: string
): FieldConfig => {
  const validations: ValidationRule[] = disabled
    ? []
    : [
        {
          type: "phone" as ValidationType,
          message: PHONE_ERROR,
        },
      ];

  const field: FieldConfig = {
    name,
    label,
    type: "text" as FieldType,
    validations,
  };

  if (disabled) {
    field.disabled = true;
    field.helperText = nonEditableHelperText;
  } else {
    field.helperText = PHONE_HELPER_TEXT;
    field.placeholder = PHONE_PLACEHOLDER;
  }

  if (isAltField) {
    field.isAltField = true;
  }

  if (fieldGroupType) {
    field.fieldGroupType = fieldGroupType;
  }

  return field;
};

/**
 * Creates a date field with optional comparison
 */
const createDateField = (
  name: string,
  label: string,
  required: boolean = false,
  compareField?: string,
  compareMessage?: string
): FieldConfig => {
  const validations: ValidationRule[] = [];

  if (required) {
    validations.push({
      type: "required" as ValidationType,
      message: `${label} is required.`,
    });
  }

  validations.push({
    type: "dateFormat" as ValidationType,
    message: DATE_FORMAT_ERROR,
  });

  if (compareField) {
    validations.push({
      type: "dateBefore" as ValidationType,
      compareField,
      message: safeGet(
        compareMessage,
        "End date must be later than or equal to start date."
      ),
    });
  }

  return {
    name,
    label,
    type: "date" as FieldType,
    required,
    helperText: DATE_HELPER_TEXT,
    placeholder: DATE_PLACEHOLDER,
    errorText: DATE_ERROR_TEXT,
    validations,
  };
};

/**
 * Creates a zip code field with validation
 */
const createZipCodeField = (
  name: string,
  fieldGroupType?: string
): FieldConfig => {
  const field: FieldConfig = {
    name,
    label: "Zip code",
    type: "text" as FieldType,
    required: false,
    helperText: ZIP_CODE_HELPER_TEXT,
    placeholder: ZIP_CODE_PLACEHOLDER,
    validations: [
      {
        type: "pattern" as ValidationType,
        value: ZIP_CODE_REGEX,
        message: ZIP_CODE_ERROR,
      },
    ],
  };
  if (fieldGroupType) {
    field.fieldGroupType = fieldGroupType;
  }
  return field;
};

/**
 * Creates a select field with required validation
 */
const createRequiredSelectField = (
  name: string,
  label: string,
  dropDownKey: string
): FieldConfig => {
  return {
    name,
    label,
    type: "select" as FieldType,
    required: true,
    placeholder: SELECT_PLACEHOLDER,
    dropDownKey,
    validations: [
      {
        type: "required" as ValidationType,
        message: `${label} is required.`,
      },
    ],
  };
};

/**
 * Creates a textarea field with max length validation
 */
const createTextareaField = (
  name: string,
  label: string,
  helperText: string,
  additionalValidations: ValidationRule[] = []
): FieldConfig => {
  const validations: ValidationRule[] = [
    {
      type: "maxLength" as ValidationType,
      value: TEXTAREA_MAX_LENGTH,
      message: TEXTAREA_MAX_ERROR(label),
    },
    ...additionalValidations,
  ];

  return {
    name,
    label,
    type: "textarea" as FieldType,
    required: false,
    helperText,
    placeholder: LOREM_PLACEHOLDER,
    maxLength: TEXTAREA_MAX_LENGTH,
    validations,
  };
};

/**
 * Creates a state select field
 */
const createStateField = (fieldGroupType?: string): FieldConfig => {
  const field: FieldConfig = {
    name: "originState",
    label: "State",
    type: "select" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: SELECT_PLACEHOLDER,
    dropDownKey: "state",
  };
  if (fieldGroupType) {
    field.fieldGroupType = fieldGroupType;
  }
  return field;
};

// ==================== STEP 1: PERSONAL INFORMATION ====================
export const personalInfoFields: FieldConfig[] = [
  {
    ...createHRField("empId", "Employee ID", "text" as FieldType),
    validations: [
      {
        type: "required" as ValidationType,
        message: "Employee ID is required.",
      },
    ],
  },
  createHRField("empNuid", "NUID"),
  createHRField("empLastName", "Last name"),
  createHRField("empFirstName", "First name"),
  createHRField("empMiddleName", "MI/Middle name"),
  createHRField("empAddress1", "Address 1"),
  createHRField("empAddress2", "Address 2"),
  createHRField("empCity", "City"),
  createHRField("empState", "State"),
  createHRField("empPostalZip", "Zip code", "text" as FieldType),
  createHRField("empHomePhone", "Home phone", "text" as FieldType),
  createHRField("empWorkPhone", "Work phone", "text" as FieldType),
  createPhoneField("empAlternatePhone", "Alternate phone"),
  {
    name: "empPrefPhone",
    label: "Preferred phone",
    type: "select" as FieldType,
    placeholder: SELECT_PLACEHOLDER,
    required: true,
    dropDownKey: "phone_type",
    validations: [
      {
        type: "required" as ValidationType,
        message: "Preferred phone is required.",
      },
    ],
  },
  {
    name: "empHomeEmail",
    label: "Home email address",
    type: "email" as FieldType,
    placeholder: "i.e. you@example.com",
    helperText: "Edit field; Must include '@' and '.com'.",
    errorText: "",
    validations: [
      {
        type: "email" as ValidationType,
        message: "Invalid email address. Please enter a valid email.",
      },
    ],
  },
  createHRField("empWorkEmail", "Work email address", "email" as FieldType),
  {
    name: "empPrefEmail",
    label: "Preferred email address",
    type: "select" as FieldType,
    placeholder: SELECT_PLACEHOLDER,
    dropDownKey: "email_type",
  },
];

// ==================== STEP 2: EMPLOYMENT INFORMATION ====================
export const employmentInfoFields: FieldConfig[] = [
  createHRField("shiftStdHours", "Standard hours", "number" as FieldType, "employmentInitial"),
  createHRField("empHireDate", "Hire date", "text" as FieldType, "employmentInitial"),
  createHRField("empRehireDate", "Rehire date", "text" as FieldType, "employmentInitial"),
  createHRField("empJobStatus", "Employee Status", "text" as FieldType, "employmentInitial"),
  createHRField("empType", "Employee type", "text" as FieldType, "employmentInitial"),
  createHRField("jobTitleCode", "Job code", "text" as FieldType, "employmentInitial"),
  createHRField("jobTitleName", "Job title", "text" as FieldType, "employmentInitial"),
  createHRField("unionName", "Union", "text" as FieldType, "employmentInitial"),
  createHRField(
    "fullPartTime",
    "Full-time or Part-time",
    "text" as FieldType,
    "employmentInitial"
  ),
  createHRField("regTemp", "Regular-Temporary", "text" as FieldType, "employmentInitial"),
  createHRField("bargUnitDesc", "Bargaining unit", "text" as FieldType, "employmentInitial"),
  createHRField("shiftId", "Employee shift", "text" as FieldType, "employmentInitial"),
];

// ==================== STEP 3: PRIMARY APPROVER INFORMATION ====================
export const approverInfoFields: FieldConfig[] = [
  // PRIMARY APPROVER FIELDS (no isAltField = primary)
  createHRField("mgrNuid", "Manager NUID", "text" as FieldType, "manager"),
  createHRField("mgrName", "Manager name", "text" as FieldType, "manager"),
  createHRField("mgrEmail", "Manager email", "email" as FieldType, "manager"),
  createPhoneField("mgrPhone", "Manager phone", false, false, "manager"),
  
  // ALTERNATE APPROVER FIELDS (isAltField = true)
  {
    ...createHRField("altNuid", "Alternative NUID", "text" as FieldType, "managerAlt"),
    isAltField: true,
  },
  {
    ...createHRField("altName", "Alternative name", "text" as FieldType, "managerAlt"),
    isAltField: true,
  },
  {
    ...createHRField("altEmail", "Alternative email", "email" as FieldType, "managerAlt"),
    isAltField: true,
  },
  {
    ...createPhoneField("altPhone", "Alternate phone", false, true, "managerAlt"),
    isAltField: true,
  },
  {
    name: "alternateNuid",
    label: "Alternate NUID",
    type: "text" as FieldType,
    required: false,
    placeholder: "Enter manager NUID",
    isSearchBtnRequired: true,
    isAltField: true,
    fieldGroupType: "managerAlt",
  },
];

// ==================== STEP 4: SCHOOL/VENDOR/COMPANY INFORMATION ====================
export const vendorInfoFields: FieldConfig[] = [
  createRequiredSelectField("programField", "Program field", "program_field"),
  createRequiredSelectField("programType", "Program type", "program_type"),

  {
    name: "fundingSource",
    label: "Application funding source",
    type: "text" as FieldType,
    required: false,
    disabled: true,
    helperText: nonEditableHelperText,
    placeholder: "KP",
  },
  {
    name: "degreeName",
    label: "Degree name",
    type: "text" as FieldType,
    required: true,
    helperText: "Edit field",
    placeholder: "i.e. Nursing, Radiology",
    validations: [
      {
        type: "requiredIf" as ValidationType,
        message: "Degree name is required.",
        condition: (formData: TuitionFormData) =>
          formData.programType !== "CEU",
      },
    ],
  },
  createDateField("termBegin", "Term start date", true),
  createDateField(
    "termEnd",
    "Term end date",
    false,
    "termBegin",
    "End date must be later than or equal to start date."
  ),
  {
    name: "applicationTerm",
    label: "Application term",
    type: "select" as FieldType,
    required: false,
    placeholder: SELECT_PLACEHOLDER,
    dropDownKey: "application_term",
  },
    {
      name: "applicationYear",
      label: "Application year",
      type: "select" as FieldType,
      required: false, // Will be set to true in admin create flow
      placeholder: SELECT_PLACEHOLDER,
      options: Array.from({ length: 11 }, (_, i) => String(new Date().getFullYear() - 5 + i)),
      validations: [
        {
          type: "required" as ValidationType,
          message: "Application year is required.",
        },
      ],
    },
  {
    name: "schoolUrl",
    label: "School contact URL",
    type: "text" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: "i.e. https://www.example.com",
    validations: [
      {
        type: "url" as ValidationType,
        message: "Invalid URL.",
      },
    ],
  },
  {
    name: "schoolName",
    label: "School name",
    type: "text" as FieldType,
    required: true,
    helperText: "If taking CEU course then enter provider/vendor name",
    placeholder: "i.e. Elementary Middle High",
    validations: [
      {
        type: "required" as ValidationType,
        message: "School name is required.",
      },
    ],
  },
  {
    name: "degreeCompletionYear",
    label: "Degree finish year",
    type: "select" as FieldType,
    required: true,
    options: Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() + i)),
    placeholder: SELECT_PLACEHOLDER,
    validations: [
      {
        type: "custom",
        validator: (value: any) => {
          // Treat empty and "--select--" as invalid, but allow "Non-Degree Program"
          if (!value || value === "--select--") {
            return false;
          }
          return true;
        },
        message: "Degree finish year is required.",
      },
    ],
  },
];

// ==================== STEP 5: CLASS INFORMATION ====================
export const courseInfoFields: FieldConfig[] = [
  {
    name: "courseName",
    label: "Course name",
    type: "text" as FieldType,
    required: true,
    maxLength: 200,
    helperText: "Edit field",
    placeholder: "",
    validations: [
      {
        type: "required" as ValidationType,
        message: "Course name is required.",
      },
      {
        type: "maxLength",
        value: 200,
        message: "Course name must not exceed 200 characters.",
      },
    ],
  },
  createRequiredSelectField("reimbursementType", "Reimbursement type", "reimbursement_type"),
  {
    name: "courseDeliveryType",
    label: "Delivery type",
    type: "select" as FieldType,
    required: true,
    placeholder: SELECT_PLACEHOLDER,
    dropDownKey: "delivery_type",
    validations: [
      {
        type: "required" as ValidationType,
        message: "Delivery type is required.",
      },
    ],
  },
  createRequiredSelectField("courseProgramType", "Course program type", "program_type"),
  createDateField("courseStartDate", "Course start date", true),
  createDateField(
    "courseEndDate",
    "Course end date",
    true,
    "courseStartDate",
    "End date must be later than start date."
  ),
  {
    name: "courseCredit",
    label: "Course credits/ units/ hours",
    type: "number" as FieldType,
    required: false,
    helperText: NUMERIC_HELPER_TEXT,
    placeholder: NUMERIC_PLACEHOLDER,
    validations: [
      {
        type: "min" as ValidationType,
        value: 0,
        message: NUMERIC_MIN_ERROR,
      },
    ],
  },
  // Fee fields
  createFeeField("estimatedTuitionFee", "Tuition fee"),
  createFeeField("estimatedRegistrationFee", "Entrance & registration fees"),
  createFeeField("estimatedBooksFee", "Books fees"),
  createFeeField("estimatedLabFee", "Lab fees"),
  createFeeField("estimatedOtherFee", "Other fees"),
  {
    name: "totalEstimatedCourseFees",
    label: "Total estimated course fees",
    type: "number" as FieldType,
    required: false,
    disabled: true,
    helperText: READONLY_HELPER_TEXT,
    placeholder: "0",
  },
  {
    name: "classSchoolName",
    label: "School name",
    type: "text" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: "i.e. Elementary Middle High",
  },
  // Travel-related fees
  createFeeField(
    "estimatedAirfareBaggageFee",
    "Airfare + baggage fees",
    "travelRelated"
  ),
  createFeeField("estimatedParkTollFee", "Parking + tolls", "travelRelated"),
  createFeeField("estimatedAutoRentalFee", "Auto rental", "travelRelated"),
  createFeeField(
    "estimatedPublicTransportFee",
    "Public transportation",
    "travelRelated"
  ),
  createFeeField("estimatedHotelFee", "Hotels", "travelRelated"),
  // Miles-related information
  {
    name: "totalNumberOfMiles",
    label: "Total number of miles (round-trip)",
    type: "number" as FieldType,
    required: false,
    helperText:
      "Total dollar value will be calculated based on the IRS standard rate",
    placeholder: "i.e. 100",
    fieldGroupType: "mileageRelated",
    validations: [
      {
        type: "min" as ValidationType,
        value: 0,
        message: NUMERIC_MIN_ERROR,
      },
    ],
  },
  {
    name: "estimatedMilesDollarValue",
    label: "Miles (round trip) dollar value",
    type: "number" as FieldType,
    disabled: true,
    required: false,
    helperText: nonEditableHelperText,
    placeholder: "i.e. 120",
    fieldGroupType: "mileageRelated",
    validations: [
      {
        type: "min" as ValidationType,
        value: 0,
        message: NUMERIC_MIN_ERROR,
      },
    ],
  },
  {
    name: "originAddress",
    label: "Mileage origin (work address)",
    type: "text" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: "i.e. 123 Main St",
    fieldGroupType: "mileageRelated",
  },
  {
    name: "originCity",
    label: "City",
    type: "text" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: "i.e. Portland",
    fieldGroupType: "mileageRelated",
  },
  {
    ...createStateField("mileageRelated"),
    name: "originState",
  },
  createZipCodeField("originZipCode", "mileageRelated"),
  {
    name: "destinationAddress",
    label: "Mileage Destination",
    type: "text" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: "i.e. School location",
    fieldGroupType: "mileageRelated",
  },
  {
    name: "destinationCity",
    label: "City",
    type: "text" as FieldType,
    required: false,
    helperText: "Edit field",
    placeholder: "i.e. Portland",
    fieldGroupType: "mileageRelated",
  },
  {
    ...createStateField("mileageRelated"),
    name: "destinationState",
  },
  createZipCodeField("destinationZipCode", "mileageRelated"),
  {
    name: "numberOfTravelDays",
    label: "Number of travel days",
    type: "number" as FieldType,
    required: false,
    helperText: NUMERIC_HELPER_TEXT,
    placeholder: "i.e. 3",
    fieldGroupType: "mileageRelated",
    validations: [
      {
        type: "requiredIf" as ValidationType,
        condition: (formData: TuitionFormData, fieldName?: string) => {
          let classIndex = 0;
          if (fieldName?.includes("_class")) {
            const regex = /_class(\d+)$/;
            const match = regex.exec(fieldName);
            if (match) {
              classIndex = Number.parseInt(match[1], 10);
            }
          }

          const hotelFeeField =
            classIndex === 0
              ? "estimatedHotelFee"
              : `estimatedHotelFee_class${classIndex}`;
          const hotelFee = formData[hotelFeeField];

          if (hotelFee && hotelFee !== "" && Number(hotelFee) > 0) {
            return true;
          }
          return false;
        },
        message:
          "Please specify the no of travel days. This is required for hotel estimates",
      },
      {
        type: "min" as ValidationType,
        value: 1,
        message: NUMERIC_MIN_ERROR,
      },
      {
        type: "max" as ValidationType,
        value: 10,
        message: "Number of travel days cannot exceed 10.",
      },
    ],
  },
];

// ==================== STEP 6: COMMENTS ====================
export const commentsFields: FieldConfig[] = [
  createTextareaField(
    "employeeComments",
    "Employee comments",
    "Briefly explain how this program will help develop your career at Kaiser Permanente."
  ),
  createTextareaField(
    "employmentJobComments",
    "Employment/Job comments",
    "Which job at KP do you plan to advance in with the completion of this program?"
  ),
  createTextareaField(
    "noEstimatedFees",
    "No estimated fees",
    "You have not entered an estimated fees or explain why you are unable to estimate the costs. Please enter fees or explain why you are unable to estimate the costs.",
    [
      {
        type: "requiredIf" as ValidationType,
        condition: (formData: TuitionFormData) => {
          const feeFields = [
            "estimatedTuitionFee",
            "estimatedRegistrationFee",
            "estimatedBooksFee",
            "estimatedLabFee",
            "estimatedOtherFee",
            "estimatedAirfareBaggageFee",
            "estimatedParkTollFee",
            "estimatedAutoRentalFee",
            "estimatedPublicTransportFee",
            "estimatedHotelFee",
            "estimatedMilesDollarValue",
          ];

          const hasBaseFees = feeFields.some((field) => {
            const value = formData?.[field];
            return (
              value !== undefined &&
              value !== null &&
              value !== "" &&
              Number(value) > 0
            );
          });

          // Check for additional classes (Class 2, 3, etc.)
          const classCount = (formData as any)?.classCount ?? 1;
          let hasAdditionalClassFees = false;

          for (let i = 1; i < classCount; i++) {
            const hasClassFees = feeFields.some((field) => {
              const classFieldName = `${field}_class${i}`;
              const value = formData?.[classFieldName];
              return (
                value !== undefined &&
                value !== null &&
                value !== "" &&
                Number(value) > 0
              );
            });
            if (hasClassFees) {
              hasAdditionalClassFees = true;
              break;
            }
          }

          return !hasBaseFees && !hasAdditionalClassFees;
        },
        message:
          "No estimated fees comment is required when no fees are entered.",
      },
    ]
  ),
];

// ==================== STEP 7: PARTICIPANTS CONDITIONS ====================
export const participantsConditionsFields: FieldConfig[] = [
  {
    name: "tuitionPolicyAgreement",
    label: "",
    type: "radio" as FieldType,
    required: true,
    options: ["Yes", "No"],
  },
  {
    name: "managerArrangements",
    label: "",
    type: "radio" as FieldType,
    required: true,
    options: ["Yes", "No"],
  },
];

// ==================== STEP CONFIGURATION ====================
export const tuitionStepTitles = {
  step1: "Applicant information",
  step2: "Employment information",
  step3: "Primary approver information",
  step4: "School/vendor/company information",
  step5: "Class information",
  step6: "",
  step7: "Participants conditions",
};

export const tuitionStepContents = {
  step1: {
    body: "All fields are optional unless marked as required. Required fields are indicated with an asterisk (*).",
  },
  step2: {
    body: "All fields are optional unless marked as required.",
  },
  step3: {
    body: "",
  },
  step4: {
    body: "All fields are optional unless marked as required. Users must fill out one field in order to proceed.",
  },
  step5: {
    body: "",
  },
  step6: {
    body: "",
  },
  step7: {
    body: "All fields are optional unless marked as required. Required fields are indicated with an asterisk (*).",
  },
};

export const tuitionStepFields = {
  step1: personalInfoFields,
  step2: employmentInfoFields,
  step3: approverInfoFields,
  step4: vendorInfoFields,
  step5: courseInfoFields,
  step6: commentsFields,
  step7: participantsConditionsFields,
};

export const fieldApiIdMap: { [key: string]: number } = {
  tuitionPolicyAgreement: 10151,
  managerArrangements: 10150,
};

export const commentTypeMap: Record<string, string> = {
  employeeComments: "EMPL_COMMENT",
  employmentJobComments: "JOB_COMMENT",
  noEstimatedFees: "No Estimated Fees",
};

export default tuitionStepFields;